import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowDownRightIcon, ArrowUpRightIcon, DollarSign, Download, EyeIcon, FileText, Pencil, Receipt, StarIcon, Users } from 'lucide-react'
import { useEffect, useState } from 'react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { adminQuote, adminQuotingQuotes, partnersQuotes } from 'rails_routes'
import { ButtonLink, Pagination, Table, Tooltip } from 'slab'
import { getGetQuotingProspectsProspectIdQuotesQuoteIdCensusUrl, usePatchQuotingProspectsProspectIdQuotesId } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { CreateQuoteButton } from './CreateQuoteButton'
import { CreateQuoteFailed } from './GetErrorBanners'
import { getTypeForCensusStatus } from './ProspectCensusDetails'
import { hasQuoteGenerationStarted } from './QuoteDetails'
import { QuoteDetailsSheet } from './QuoteDetailsSheet'
import { QuoteFeatureModal } from './QuoteFeatureModal'
import { QuoteFeatureToggle } from './QuoteFeatureToggle'
import { type Quote } from './types'
import { getFormattedDate, getLocaleDateString, getTimezoneDatetimeString } from '../../utils'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { useGetQuotes } from '../hooks/useGetQuotes'
import Badge from '../reusable/Badge'
import TanstackTable, {
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<Quote>()
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => {
      const value = info.getValue()
      if (!value) {
        const quoteDate = info.row.original.created_at
          ? new Date(info.row.original.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
          : null
        return `${info.row.original.quoting_prospect_name} quote ${quoteDate}`
      }

      return value.length > 30 ? `${value.substring(0, 30)}...` : value
    },
  }),
  columnHelper.accessor(
    (row) => ({
      content: (
        <div>
          <div>
            {getTimezoneDatetimeString(new Date(row.created_at), 'EST')}
          </div>
          <div className='caption text-gray-500'>
            {row.census_file_name}
          </div>
        </div>
      ),
    }),
    {
      header: 'Date created (EST) & census',
      id: 'date_created_and_census',
      cell: (info) => info.getValue().content,
    }
  ),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => (
      <Badge
        type={
          info.getValue() ? getTypeForCensusStatus(info.getValue()!) : 'default'
        }
        text={info.getValue() ?? 'Unknown'}
      />
    ),
  }),
  columnHelper.accessor('coverage_start_date', {
    header: 'Coverage start date',
    cell: (info) =>
      info.getValue()
        ? getLocaleDateString(new Date(info.getValue()!))
        : undefined,
  }),
]

const PAGE_SIZE = 10

function CreateNewQuoteCover ({
  isForAdminView,
  prospectId,
  onCreateQuoteError,
  quotingV2NewQuotePath,
}: {
  isForAdminView: boolean;
  prospectId: string;
  onCreateQuoteError: (error: GrapeAPIErrorType) => void;
  quotingV2NewQuotePath?: string;
}) {
  const handleCreateQuoteError = (error: GrapeAPIErrorType) => {
    onCreateQuoteError(error)
  }

  return (
    <>
      <p>No quotes have been created for this prospect yet.</p>
      {isForAdminView && (
        <>
          <p className='mb-4'>
            To get started, click "Create new quote" below.
          </p>
          {quotingV2NewQuotePath
            ? (
              <ButtonLink
                href={quotingV2NewQuotePath}
                variant='secondary'
                leftIcon={Receipt}
              >
                Create new quote
              </ButtonLink>
              )
            : <CreateQuoteButton
                prospectId={prospectId}
                onShowError={handleCreateQuoteError}
                secondary
              />}
        </>
      )}
      {!isForAdminView && (
        <div className='mt-4'>
          <ButtonLink
            href={partnersQuotes.new({ prospect_id: prospectId })}
            variant='secondary'
            leftIcon={Receipt}
          >
            Create new quote
          </ButtonLink>
        </div>
      )}
    </>
  )
}

export function QuotesList ({
  isForAdminView,
  prospectId,
  quotingV2NewQuotePath,
  quotingV2DynamicQuoteEditorPath,
  quotingV2FlatQuoteEditorPath,
}: {
  isForAdminView: boolean;
  prospectId: string;
  quotingV2NewQuotePath?: string;
  quotingV2DynamicQuoteEditorPath?: string;
  quotingV2FlatQuoteEditorPath?: string;
}) {
  const { assign } = useWindowLocation()

  const [showDetail, setShowDetail] = useState<string | undefined>()
  const [createQuoteError, setCreateQuoteError] =
    useState<GrapeAPIErrorType | null>(null)

  const handleCreateQuoteError = (errorObj: GrapeAPIErrorType) => {
    setCreateQuoteError(errorObj)
  }

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isLoading, isError } = useGetQuotes({
    prospectId,
    pagination,
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  return (
    <>
      {showDetail && (
        <QuoteDetailsSheet
          prospectId={prospectId}
          quoteId={showDetail}
          onClose={() => setShowDetail(undefined)}
          quotingV2DynamicQuoteEditorPath={quotingV2DynamicQuoteEditorPath}
          quotingV2FlatQuoteEditorPath={quotingV2FlatQuoteEditorPath}
        />
      )}
      {createQuoteError && (
        <CreateQuoteFailed className='mb-4' errorObj={createQuoteError} />
      )}
      <TanstackTable<Quote>
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage={
          <CreateNewQuoteCover
            isForAdminView={isForAdminView}
            prospectId={prospectId}
            onCreateQuoteError={handleCreateQuoteError}
            quotingV2NewQuotePath={quotingV2NewQuotePath}
          />
        }
        onRowClick={({ row }) => {
          if (hasQuoteGenerationStarted(row.original) || row.original.has_flat_allowances) {
            setShowDetail(row.original.id)
          } else {
            const recordId = row.original.id
            assign(`${prospectId}/quote/${recordId}`)
          }
        }}
        paginationLabelSingular='quote'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
    </>
  )
}

function QuoteCard ({
  isForAdminView,
  quote,
  onClick,
}: {
  isForAdminView: boolean;
  quote: Quote;
  onClick: () => void;
}) {
  const {
    created_at: createdAt,
    eligible_employee_count: eligibleEmployeeCount,
    census_file_name: censusFileName,
  } = quote

  const getQuoteDisplayName = () => {
    if (quote.name) {
      return quote.name
    }

    const quoteDate = createdAt
      ? new Date(createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
      : null
    return `${quote.quoting_prospect_name} quote ${quoteDate}`
  }

  return (
    <div
      className='flex items-center justify-between p-4 border border-ds-divider rounded-md mb-3 bg-white shadow-sm cursor-pointer hover:bg-ds-background-hover'
      onClick={onClick}
      role='button'
      tabIndex={0}
    >
      <div className='flex flex-col'>
        <div className='font-medium text-ds-text-primary'>
          {getQuoteDisplayName()}
        </div>
        <div className='flex items-center flex-wrap gap-x-3 caption text-gray-500 mt-1'>
          {quote.total_monthly_employer_contribution != null && (
            <span className='flex items-center gap-x-1 whitespace-nowrap'>
              <DollarSign size={12} />
              {quote.total_monthly_employer_contribution.formatted_amount.replace(/^\$/, '')} / mo.
            </span>
          )}
          {eligibleEmployeeCount != null && (
            <span className='flex items-center gap-x-1 whitespace-nowrap'>
              <Users size={12} />
              {eligibleEmployeeCount} eligible employees
            </span>
          )}
          {censusFileName && (
            <span className='flex items-center gap-x-1 whitespace-nowrap'>
              <FileText size={12} />
              {censusFileName}
            </span>
          )}
        </div>
      </div>

      <div className='flex items-center justify-end gap-x-2 flex-shrink-0 ml-4'>
        {quote.status === 'unprocessed' && !quote.has_flat_allowances && (
          <ButtonLink
            href={`/${isForAdminView ? 'admin' : 'broker'}/prospects/${quote.quoting_prospect_id}/quotes/${quote.id}/edit`}
            variant='secondary'
            size='small'
            leftIcon={Pencil}
          >
            Complete Quote
          </ButtonLink>
        )}
        {quote.status !== 'unprocessed' && (
          <div className='flex gap-x-2'>
            <ButtonLink
              href={`/${isForAdminView ? 'admin' : 'broker'}/prospects/${quote.quoting_prospect_id}/quotes/${quote.id}/edit`}
              variant='secondary'
              size='small'
              leftIcon={Pencil}
            >
              Edit
            </ButtonLink>
            <ButtonLink
              href={`/${isForAdminView ? 'admin' : 'broker'}/prospects/${quote.quoting_prospect_id}/quotes/${quote.id}/${quote.has_flat_allowances ? 'flat_allowances' : 'explore'}`}
              variant='secondary'
              size='small'
              leftIcon={EyeIcon}
            >
              View
            </ButtonLink>
          </div>
        )}
        {/* TODO: Pull the share modal out into a separate page that we can link to directly */}
        {/* <ButtonLink href={partnersQuotes.explore({ prospect_id: quote.quoting_prospect_id, quote_id: quote.id })} variant='secondary' size='small' leftIcon={Share}>
          Share
        </ButtonLink> */}
      </div>

    </div>
  )
}

export function QuotesListV2 ({
  isForAdminView,
  prospectId,
  quotingV2NewQuotePath,
}: {
  isForAdminView: boolean;
  prospectId: string;
  quotingV2NewQuotePath?: string;
  quotingV2QuoteEditorPath?: string;
}) {
  const { assign } = useWindowLocation()

  const [createQuoteError, setCreateQuoteError] =
    useState<GrapeAPIErrorType | null>(null)

  const handleCreateQuoteError = (errorObj: GrapeAPIErrorType) => {
    setCreateQuoteError(errorObj)
  }

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isLoading, isError } = useGetQuotes({
    prospectId,
    pagination,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  const handleQuoteClick = (quote: Quote) => {
    const recordId = quote.id
    if (quote.status === 'unprocessed') {
      assign(`/${isForAdminView ? 'admin' : 'broker'}/prospects/${prospectId}/quotes/${recordId}/edit`)
    } else if (quote.has_flat_allowances) {
      assign(`/${isForAdminView ? 'admin' : 'broker'}/prospects/${prospectId}/quotes/${recordId}/flat_allowances`)
    } else {
      assign(`/${isForAdminView ? 'admin' : 'broker'}/prospects/${prospectId}/quotes/${recordId}/explore`)
    }
  }

  return (
    <>
      {createQuoteError && (
        <CreateQuoteFailed className='mb-4' errorObj={createQuoteError} />
      )}

      {isLoading && <p>Loading quotes...</p>}

      {isError && !isLoading && <p>Error loading quotes.</p>}

      {!isLoading && !isError && data && (
        <>
          {data.data.length === 0 && (
            <CreateNewQuoteCover
              isForAdminView={isForAdminView}
              prospectId={prospectId}
              onCreateQuoteError={handleCreateQuoteError}
              quotingV2NewQuotePath={quotingV2NewQuotePath}
            />
          )}
          {data.data.map((quote) => (
            <QuoteCard
              isForAdminView={isForAdminView}
              key={quote.id}
              quote={quote}
              onClick={() => handleQuoteClick(quote)}
            />
          ))}

          {paginationMetadata && paginationMetadata.total_pages > 1 && (
            <div className='flex justify-between items-center mt-4'>
              <ButtonLink
                onClick={() =>
                  setPagination({
                    ...pagination,
                    pageIndex: paginationMetadata?.prev_page || 1,
                  })}
                disabled={!paginationMetadata?.prev_page}
                variant='tertiary'
                size='small'
              >
                Previous
              </ButtonLink>
              <span>
                Page {paginationMetadata.current_page} of {paginationMetadata.total_pages}
              </span>
              <ButtonLink
                onClick={() =>
                  setPagination({
                    ...pagination,
                    pageIndex: paginationMetadata?.next_page || paginationMetadata.total_pages,
                  })}
                disabled={!paginationMetadata?.next_page}
                variant='tertiary'
                size='small'
              >
                Next
              </ButtonLink>
            </div>
          )}
        </>
      )}
    </>
  )
}

export function QuotesTable ({
  isForAdminView,
  prospectId,
  prospectName,
}: {
  isForAdminView: boolean
  prospectId: string
  prospectName?: string
}) {
  const { assign } = useWindowLocation()
  const [quoteToFeature, setQuoteToFeature] = useState<Quote>()
  const [isQuoteFeatureModalOpen, setQuoteFeatureModalOpen] = useState(false)

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  })

  const paginationForQuery = {
    ...pagination,
    // `slab/Pagination` uses 0-based pageIndex, while our API expects 1-based page[number].
    pageIndex: pagination.pageIndex + 1,
  }

  const { data: quotesData, isLoading, isError, refetch: refetchQuotes } = useGetQuotes({
    prospectId,
    pagination: paginationForQuery,
  })

  const { mutate: updateQuote } = usePatchQuotingProspectsProspectIdQuotesId({
    mutation: {
      onSuccess: () => {
        refetchQuotes()
        setQuoteFeatureModalOpen(false)
      }
    }
  })

  useEffect(() => {
    if (quotesData) {
      setPaginationMetadata(quotesData?.pagination)
    }
  }, [quotesData?.pagination])

  const handleQuoteClick = (quote: Quote) => () => {
    const params = { prospect_id: prospectId, id: quote.id, quote_id: quote.id }
    if (isForAdminView) {
      const page = quote.status === 'unprocessed'
        ? adminQuotingQuotes.edit
        : adminQuote.explore // Routing from here is handled by controller redirects
      assign(page(params))
    } else {
      const page = quote.status === 'unprocessed'
        ? partnersQuotes.edit
        : quote.has_flat_allowances
          ? partnersQuotes.flatAllowances
          : partnersQuotes.explore
      assign(page(params))
    }
  }

  const onFeatureQuoteChange = (id: string, featured: boolean) => () =>
    updateQuote({
      prospectId,
      id,
      data: { featured }
    })

  return (
    <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden p-4'>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.ActionColumnHeader />
            <Table.ColumnHeader>Quote</Table.ColumnHeader>
            <Table.ColumnHeader>Date created</Table.ColumnHeader>
            <Table.ColumnHeader align='right'>Eligible employees</Table.ColumnHeader>
            <Table.ColumnHeader align='right'>Current premiums</Table.ColumnHeader>
            <Table.ColumnHeader align='right'>Quoted premiums</Table.ColumnHeader>
            {isForAdminView && <Table.ActionColumnHeader />}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {isLoading &&
            <tr>
              <td className='h-16' colSpan={5}>
                <LoadingSpinnerCentered />
              </td>
            </tr>}

          {isError && !isLoading &&
            <tr>
              <td className='h-16' colSpan={5}>
                <p className='text-center'>Error loading quotes.</p>
              </td>
            </tr>}

          {!isLoading && !isError && quotesData?.data.map((quote) => {
            const currentPremium = quote.current_total_monthly_contribution
            const quotedPremium = quote.total_monthly_employer_contribution

            return (
              <Table.Row key={quote.id} accessibleLabel={quote.name ?? ''} onClick={handleQuoteClick(quote)}>
                <Table.Cell align='center' width={50}>
                  <div className='flex items-center'>

                    {isForAdminView
                      ? (
                        <Tooltip content='Featured'>
                          {/* TODO: replace this toggle with a Slab toggle when that's built */}
                          <QuoteFeatureToggle
                            quoteId={quote.id}
                            toggled={quote.featured}
                            onToggle={(newFeatured) => {
                              if (newFeatured) {
                                setQuoteToFeature(quote)
                                setQuoteFeatureModalOpen(true)
                              } else {
                                onFeatureQuoteChange(quote.id, false)()
                              }
                            }}
                          />
                        </Tooltip>)
                      : (quote.featured &&
                        <Tooltip content='Featured'>
                          <StarIcon
                            size={20}
                            className='text-gray-500 fill-yellow-300'
                          />
                        </Tooltip>)}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className='sensitive'>
                    {quote.name}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span>{getFormattedDate(quote.created_at)}</span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span>{quote.eligible_employee_count}</span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span>{currentPremium?.formatted_amount}</span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span className='flex gap-1 w-full justify-end items-center'>
                    {(currentPremium && quotedPremium) && (quotedPremium.subunits > currentPremium.subunits
                      ? <ArrowUpRightIcon className='w-4 h-4 text-red-400' data-testid='arrow-up-right-icon' />
                      : <ArrowDownRightIcon className='w-4 h-4 text-green-400' data-testid='arrow-down-right-icon' />)}
                    {quotedPremium?.formatted_amount}
                  </span>
                </Table.Cell>
                {isForAdminView &&
                  <Table.ActionsCell>
                    <ButtonLink
                      variant='secondary'
                      rightIcon={Download}
                      href={getGetQuotingProspectsProspectIdQuotesQuoteIdCensusUrl(prospectId, quote.id)}
                    >
                      Census
                    </ButtonLink>
                  </Table.ActionsCell>}
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table>
      <div className='flex justify-end p-2'>
        {paginationMetadata &&
          <Pagination
            pageIndex={pagination.pageIndex}
            perPage={pagination.pageSize}
            totalRows={paginationMetadata.total_records}
            onPageChange={(pageIndex) => setPagination({ ...pagination, pageIndex })}
          />}
      </div>
      {quoteToFeature &&
        <QuoteFeatureModal
          prospectName={prospectName}
          quote={quoteToFeature}
          open={isQuoteFeatureModalOpen}
          onOpenChange={setQuoteFeatureModalOpen}
          onAccept={onFeatureQuoteChange(quoteToFeature.id, true)}
        />}
    </div>
  )
}
